import React, {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useContext,
  useState,
} from 'react'
import { Matrix4, Vector3 } from 'three'

/**
 * Minimal context for the standalone 3D tiles viewer.
 * Provides the tile matrix and ellipsoid state that the viewer expects.
 */
export interface ReadOnly3DTilesContextType {
  tilesMatrix: Matrix4 | undefined
  setTilesMatrix: Dispatch<SetStateAction<Matrix4 | undefined>>
  tilesEllipsoid: TilesEllipsoid | undefined
  setTilesEllipsoid: Dispatch<SetStateAction<TilesEllipsoid | undefined>>
}

type TilesEllipsoid = {
  getCartographicToPosition?: (
    latRad: number,
    lonRad: number,
    height: number,
    out: Vector3
  ) => Vector3
}

const ReadOnly3DTilesContext = createContext<
  ReadOnly3DTilesContextType | undefined
>(undefined)

export const useReadOnly3DTiles = (): ReadOnly3DTilesContextType => {
  const context = useContext(ReadOnly3DTilesContext)
  if (!context) {
    throw new Error(
      'useReadOnly3DTiles must be used within a ReadOnly3DTilesProvider'
    )
  }
  return context
}

interface ReadOnly3DTilesProviderProps {
  children: ReactNode
}

export const ReadOnly3DTilesProvider: FC<ReadOnly3DTilesProviderProps> = ({
  children,
}) => {
  const [tilesMatrix, setTilesMatrix] = useState<Matrix4 | undefined>(undefined)
  const [tilesEllipsoid, setTilesEllipsoid] = useState<
    TilesEllipsoid | undefined
  >(undefined)

  return (
    <ReadOnly3DTilesContext.Provider
      value={{
        tilesMatrix,
        setTilesMatrix,
        tilesEllipsoid,
        setTilesEllipsoid,
      }}>
      {children}
    </ReadOnly3DTilesContext.Provider>
  )
}
